<!-- eslint-disable max-len -->
<template>
  <div id='mapView'>
    <v-overlay color="#fff" :value='checkLoading()'>
      <Cog />
    </v-overlay>
    <MapController />
    <MapContainer />
    <ErrorModal :dialog='error' :message='getErrorMessage()' :update='updateErrorModal' />
    <v-card :elevation='5' v-if='getScatterToggle()'>
      <v-system-bar color='#fff' height='auto'>
        <v-spacer></v-spacer>
        <v-btn depressed rounded small color='primary' class='ma-1' @click="directToDataset()">View Full Dataset</v-btn>
        <ModalBtn :singular='true' :networkProvider='selectedLocation ? selectedLocation.area : "N/A"'
          :localAuthority='selectedLocation ? selectedLocation.provider : "N/A"'
          :location='selectedLocation ? selectedLocation.location : "N/A"' type='counters'
          :download='formatModalOptions' :displayToggle='false' />
        <v-icon @click='disposeOfChart' color='#67b7dc'>mdi-close-circle</v-icon>
      </v-system-bar>
      <ScatterChart id="scatterChart" :disposeChart='disposeChart' :title='selectedLocation.location'
        :data='fetchScatterData()' />
    </v-card>
    <v-card :elevation='5' v-if='getLogToggle()'>
      <v-toolbar color='#fff' height='auto' class="mx-auto pa-2" depressed>
        <v-spacer></v-spacer>
        <v-spacer></v-spacer>
        <v-select v-model='logRange' :items='getLogarithmicRanges()' height="10" dense hide-details item-text="from"
          item-value="from" label='Data Range' return-object outlined>
          <template slot="selection" slot-scope="data">
            <template>
              <v-list-item ripple>
                <v-list-item-content style="justify-content: center;">
                  {{ data.item.from }} - {{ data.item.to }}
                </v-list-item-content>
              </v-list-item>
            </template>
            <template slot="item" slot-scope="data">
              <v-list-item>
                <v-list-item-content style="justify-content: center;">
                  {{ data.item.from }} - {{ data.item.to }}
                </v-list-item-content>
              </v-list-item>
            </template>
          </template>
        </v-select>
        <v-spacer></v-spacer>
        <v-btn depressed rounded small color='primary' class='ma-1'
          @click='directToDataset("https://usmart.io/org/cyclingscotland/discovery/discovery-view-detail/b9fdd462-1f45-4252-aedc-fbc596abcd90")'>
          View Full Dataset</v-btn>
        <ModalBtn :singular='true' :networkProvider='selectedLocation ? selectedLocation.Settlement : "N/A"'
          :localAuthority='"Cycling Scotland"' :location='selectedLocation ? selectedLocation.Street : "N/A"'
          type='surveys' :download='formatModalOptions' :displayToggle='false' />
        <v-icon @click='disposeOfChart' color='#67b7dc'>mdi-close-circle</v-icon>
      </v-toolbar>
      <LogarithmicChart :id='"Log Chart"' :title='selectedLocation ? selectedLocation.Street : "N/A"'
        :disposeChart='disposeChart' :data='fetchlogarithmicData()' :colors="colors" />
    </v-card>
  </div>
</template>

<script>
// @ is an alias to /src
import { mapActions, mapGetters, mapMutations } from 'vuex';
import moment from 'moment';
import MapContainer from '@/components/Map/MapContainer.vue';
import MapController from '@/components/Map/MapController.vue';
import ScatterChart from '@/components/Scatter/ScatterChart.vue';
import LogarithmicChart from '@/components/Logarithmic/LogarithmicChart.vue';
import ErrorModal from '@/components/Modal/ErrorModal.vue';
import ModalBtn from '@/components/Modal/Modal.vue';
import Cog from '@/components/animated/cog.vue';

export default {
  name: 'MapView2',
  components: {
    MapContainer,
    MapController,
    ScatterChart,
    LogarithmicChart,
    ErrorModal,
    ModalBtn,
    Cog,
  },
  data() {
    return {
      disposeChart: false,
      dateRange: '',
      colors: ['#00b0db', '#d4d0c7', '#fdc300', '#6fbc85', '#ed6d91', '#143c6a'],
      location: {
        area: '',
        provider: '',
        place: '',
      },
    };
  },
  created() {
    this.requestAutomaticCyclingCounters({ operators: ['limit(-1)', 'distinct(location)', 'cache=true'] });
    this.requestTrafficSurveyData({ operators: ['limit(-1)', 'distinct(Street)', 'cache=true'] });
  },
  methods: {
    ...mapActions([
      'requestAutomaticCyclingCounters',
      'requestTrafficSurveyData',
      'requestDownloads',
      'requestTrafficSurveyDownload',
      'requestUpdateToLogData',
      'singularDownload',
    ]),
    ...mapMutations(['toggleScatter', 'toggleLog']),
    ...mapGetters([
      'fetchScatterData',
      'getScatterToggle',
      'getLogToggle',
      'fetchlogarithmicData',
      'getErrorMessage',
      'checkLoading',
      'getLogarithmicRanges',
      'getDataLink',
    ]),
    directToDataset(link) {
      if (link) return window.open(link, '_blank');
      const cylceCounterLink = this.getDataLink();
      return window.open(cylceCounterLink, '_blank');
    },
    disposeOfChart() {
      this.disposeChart = true;
      this.$store.commit('toggleLoading', true);
      setTimeout(() => {
        this.toggleScatter(false);
        this.toggleLog(false);
        this.$store.commit('toggleLoading', false);
      }, 10);
    },
    updateErrorModal(val) {
      this.error = val;
    },
    async formatModalOptions(startTime, endTime, type, singular) {
      this.$store.commit('toggleLoading', true);
      let responses = [];
      // choose the type to handle the downloads correctly
      switch (type) {
        case 'counters':
          if (singular) {
            responses = await this.singularDownload({
              operators: [
                'limit(-1)',
                `ge(startTime,${startTime})`,
                `le(endTime,${endTime})`,
                `eq(latitude,${this.selectedLocation.latitude})`,
                `eq(longitude,${this.selectedLocation.longitude})`,
                'format(csv)',
              ],
              startTime,
              endTime,
            });
          } else {
            responses = await this.requestDownloads({
              operators: [
                'limit(-1)',
                `ge(StartDateTime,${startTime})`,
                `le(endDateTime,${endTime})`,
                `eq(latitude,${this.selectedLocation.latitude})`,
                `eq(longitude,${this.selectedLocation.longitude})`,
                'format(csv)',
              ],
              startTime,
              endTime,
            });
          }
          break;
        case 'surveys': {
          const flattenedStartTime = moment(startTime, 'YYYY-MM-DD')
            .clone()
            .startOf('month')
            .format('YYYY-MM-DD');
          const flattenedEndTime = moment(endTime, 'YYYY-MM-DD')
            .clone()
            .endOf('month')
            .format('YYYY-MM-DD');
          const response = await this.requestTrafficSurveyDownload({
            operators: [
              'limit(-1)',
              `ge(StartDateTime,${flattenedStartTime})`,
              `le(EndDateTime,${flattenedEndTime})`,
              `eq(Latitude,${this.selectedLocation.Latitude})`,
              `eq(Longitude,${this.selectedLocation.Longitude})`,
              'format(csv)',
            ],
          });
          Object.assign(response, {
            filename: `trafficSurveys_${flattenedStartTime}_${flattenedEndTime}`,
          });
          responses = [response];
          break;
        }
        default:
          this.$store.commit('setError', {
            error: 'There was no type to handle download processing',
          });
      }
      responses.forEach((response) => {
        try {
          if (response.data.size === 0) {
            this.$store.commit('toggleLoading', false);
            this.$store.commit('setError', {
              error: 'There is no data for the selected parameters. Please try another date range.',
            });
          } else {
            const blob = new Blob([response.data], { type: 'application/csv' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${response.filename}.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
          }
        } catch (e) {
          this.$store.commit('toggleLoading', false);
          this.$store.commit('setError', { error: e.message });
        }
      });
      this.$store.commit('toggleLoading', false);
    },
  },
  computed: {
    error: {
      get() {
        return this.$store.state.map2.displayError;
      },
      set(val) {
        this.$store.commit('closeErrorModal', val);
      },
    },
    authority: {
      get() {
        return this.$store.state.map2.selectedAuthority;
      },
      set(val) {
        this.$store.commit('setAuthority', val);
      },
    },
    network: {
      get() {
        return this.$store.state.map2.selectedNetwork;
      },
      set(val) {
        this.$store.commit('setNetwork', val);
      },
    },
    logRange: {
      get() {
        return this.$store.state.map2.selectedLogRange;
      },
      set(val) {
        this.$store.commit('setLogRange', val);
      },
    },
    selectedLocation: {
      get() {
        return this.$store.state.map2.selectedLocation;
      },
    },
  },
  watch: {
    getScatterToggle(val) {
      if (val) {
        this.disposeChart = false;
      }
    },
    getLogToggle(val) {
      if (val) {
        this.disposeChart = false;
      }
    },
    logRange(val, oldVal) {
      if (oldVal && val) {
        this.requestUpdateToLogData(val);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#mapView {
  display: flex;
  flex-direction: column;
  height: 100%;
}
</style>
